import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * `undefined` means no private boundary (public pages keep the body default),
 * while `null` means the private boundary exists but its safe DOM target has
 * not committed yet. Keeping those states distinct prevents a first-render
 * fallback to `document.body` from briefly escaping the principal veil.
 */
const PrivatePortalTargetContext = React.createContext<HTMLElement | null | undefined>(
  undefined,
);

interface PrivatePortalBoundaryProps {
  children: React.ReactNode;
  veiled: boolean;
}

/** One principal-owned DOM boundary for both the Shell and all of its portals. */
export function PrivatePortalBoundary({ children, veiled }: PrivatePortalBoundaryProps) {
  const [portalTarget, setPortalTarget] = React.useState<HTMLDivElement | null>(null);

  return (
    <PrivatePortalTargetContext.Provider value={portalTarget}>
      <div
        data-principal-veil={veiled ? 'verifying' : 'verified'}
        hidden={veiled}
        inert={veiled}
        aria-hidden={veiled || undefined}
        style={{ display: veiled ? 'none' : 'contents' }}
      >
        {children}
        <div ref={setPortalTarget} data-private-portal-root className="contents" />
      </div>
    </PrivatePortalTargetContext.Provider>
  );
}

/** Shared Radix wrappers use this to keep their portal under the private veil. */
export function usePrivatePortalTarget(): HTMLElement | null | undefined {
  return React.useContext(PrivatePortalTargetContext);
}

/** Private-aware replacement for direct `createPortal(..., document.body)`. */
export function PrivatePortal({ children }: { children: React.ReactNode }) {
  const target = usePrivatePortalTarget();
  if (target === null || typeof document === 'undefined') return null;
  return createPortal(children, target ?? document.body);
}
